import {
  PLAYABLE_THEATERS,
  advanceCampaign,
  defaultCampaign,
  isRunTheater,
  totalClears,
  unlockedNodeKinds,
  type CampaignState,
} from "./campaign";
import { fieldModById } from "./field-mods";
import {
  defaultProfileRoster,
  freshRecruitName,
  generateRoster,
  type OperatorRecord,
} from "./operators";
import { defaultMail, normalizeMail, type MailState } from "./mail";
import { generateRunMap } from "./run-map";
import { BASE_SQUAD_SIZE, type NodeKind, type RunState, type RunTheater } from "./run-types";
import { armoryUnlockById } from "./unlocks";

const SAVE_KEY = "c130_save_v1"; // key kept stable; payload carries `version`

// v1 shipped a permanent skill shop. Its nodes are retired; on migration the
// points spent on them are refunded so long-time saves start the Armory era
// with their earnings intact. Frozen here — the live skill tree is gone.
const LEGACY_NODE_COSTS: Record<string, number> = {
  "cg-rate": 2, "cg-spread": 3, "cg-tracer": 2,
  "ac-rate": 2, "ac-blast": 3, "ac-incend": 4,
  "how-rate": 3, "how-fuze": 3, "how-cluster": 5,
};

export type CareerStats = {
  missions: number;
  kills: number;
  squadLost: number;
  runsCompleted: number;
  runsFailed: number;
  bestDepth: number;
};

// One bulletin-board card per finished Operation (docs/design/HUB.md §Hub
// Ambient Systems) — by mid-campaign the board is a visual history.
export type AfterActionRecord = {
  day: number; // campaign day the Operation ended
  theater: RunTheater;
  outcome: "victory" | "defeat";
  depth: number; // nodes cleared
  ops: number;
  kills: number;
  fallen: string[];
};

const LOG_LIMIT = 8;

export type SaveData = {
  version: 3;
  opsPoints: number;
  armoryUnlocks: string[];
  career: CareerStats;
  // The persistent named roster (docs/design/GDD.md §The Squad Roster)
  operators: OperatorRecord[];
  // World state between Operations: escalation, NG+ clears, campaign day
  campaign: CampaignState;
  // Most recent after-action cards, newest first
  log: AfterActionRecord[];
  // The Mail Bag: pull ritual + the accumulating care-package pile
  mail: MailState;
  activeRun: RunState | null;
};

function defaults(): SaveData {
  return {
    version: 3,
    opsPoints: 0,
    armoryUnlocks: [],
    career: { missions: 0, kills: 0, squadLost: 0, runsCompleted: 0, runsFailed: 0, bestDepth: 0 },
    operators: defaultProfileRoster(),
    campaign: defaultCampaign(),
    log: [],
    mail: defaultMail(),
    activeRun: null,
  };
}

function normalizeOperators(value: unknown): OperatorRecord[] {
  if (!Array.isArray(value)) return defaultProfileRoster();
  const seen = new Set<string>();
  const out: OperatorRecord[] = [];
  for (const item of value) {
    if (!isRecord(item) || typeof item.name !== "string" || item.name.length === 0 || item.name.length > 24) continue;
    if (seen.has(item.name)) continue;
    seen.add(item.name);
    const woundedRuns = asWholeNumber(item.woundedRuns, 0);
    out.push({
      name: item.name,
      xp: asWholeNumber(item.xp, 0),
      status: item.status === "wounded" && woundedRuns > 0 ? "wounded" : "ready",
      woundedRuns: item.status === "wounded" ? woundedRuns : 0,
    });
  }
  // Backfill to full strength (pre-roster saves, corrupt entries)
  while (out.length < defaultProfileRoster().length) {
    const name = freshRecruitName([...seen]);
    seen.add(name);
    out.push({ name, xp: 0, status: "ready", woundedRuns: 0 });
  }
  return out;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asWholeNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : fallback;
}

function asStringArray(value: unknown, keep: (item: string) => boolean): string[] {
  if (!Array.isArray(value)) return [];
  const unique = new Set<string>();
  for (const item of value) {
    if (typeof item === "string" && keep(item)) unique.add(item);
  }
  return [...unique];
}

function normalizeCampaign(value: unknown): CampaignState {
  const base = defaultCampaign();
  if (!isRecord(value)) return base;
  const theaters = { ...base.theaters };
  if (isRecord(value.theaters)) {
    for (const key of PLAYABLE_THEATERS) {
      const t = value.theaters[key];
      if (!isRecord(t)) continue;
      theaters[key] = {
        escalation: Math.min(asWholeNumber(t.escalation, 0), 9),
        clears: asWholeNumber(t.clears, 0),
      };
    }
  }
  return { day: asWholeNumber(value.day, 0), theaters };
}

function normalizeLog(value: unknown): AfterActionRecord[] {
  if (!Array.isArray(value)) return [];
  const out: AfterActionRecord[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const theater = item.theater;
    if (!isRunTheater(theater)) continue;
    out.push({
      day: asWholeNumber(item.day, 0),
      theater,
      outcome: item.outcome === "victory" ? "victory" : "defeat",
      depth: asWholeNumber(item.depth, 0),
      ops: asWholeNumber(item.ops, 0),
      kills: asWholeNumber(item.kills, 0),
      fallen: asStringArray(item.fallen, (n) => n.length > 0 && n.length <= 24),
    });
    if (out.length >= LOG_LIMIT) break;
  }
  return out;
}

function normalizeCareer(value: unknown): CareerStats {
  const c = isRecord(value) ? value : {};
  return {
    missions: asWholeNumber(c.missions, 0),
    kills: asWholeNumber(c.kills, 0),
    squadLost: asWholeNumber(c.squadLost, 0),
    runsCompleted: asWholeNumber(c.runsCompleted, 0),
    runsFailed: asWholeNumber(c.runsFailed, 0),
    bestDepth: asWholeNumber(c.bestDepth, 0),
  };
}

// A persisted run is only trusted if it round-trips: the node graph is
// regenerated from the seed (never persisted), and every reference must
// resolve against it. `allowedKinds` comes from the campaign, which cannot
// change while a run is active (it only advances at bankRun) — so the
// regenerated graph matches what the player saw.
function normalizeRun(value: unknown, allowedKinds: ReadonlySet<NodeKind>): RunState | null {
  if (!isRecord(value)) return null;
  const seed = value.seed;
  if (typeof seed !== "number" || !Number.isFinite(seed)) return null;

  let nodes;
  try {
    nodes = generateRunMap(seed >>> 0, allowedKinds);
  } catch {
    return null;
  }
  const nodeIds = new Set(nodes.map((n) => n.id));

  const currentNodeId = typeof value.currentNodeId === "string" && nodeIds.has(value.currentNodeId)
    ? value.currentNodeId
    : null;
  const outcome = value.outcome === "victory" || value.outcome === "defeat" ? value.outcome : "active";
  const squadMax = Math.max(1, asWholeNumber(value.squadMax, BASE_SQUAD_SIZE));
  const squadSize = Math.min(squadMax, Math.max(1, asWholeNumber(value.squadSize, BASE_SQUAD_SIZE)));

  const isName = (n: string) => n.length > 0 && n.length <= 24;
  const fallen = asStringArray(value.fallen, isName);
  const wipeLosses = asStringArray(value.wipeLosses, (n) => isName(n) && fallen.includes(n));
  // Roster must be squadSize names disjoint from the fallen; a save that
  // predates rosters (or is corrupt) regenerates deterministically.
  let roster = asStringArray(value.roster, isName).filter((n) => !fallen.includes(n)).slice(0, squadSize);
  if (roster.length !== squadSize) {
    roster = generateRoster(seed >>> 0, squadMax).filter((n) => !fallen.includes(n)).slice(0, squadSize);
    while (roster.length < squadSize) roster.push(`REPL-${(roster.length + 1).toString().padStart(2, "0")}`);
  }
  const reserves = asStringArray(value.reserves, isName)
    .filter((n) => !roster.includes(n) && !fallen.includes(n));

  const theater = isRunTheater(value.theater) ? value.theater : "stone-wall";

  return {
    seed: seed >>> 0,
    theater,
    nodes,
    currentNodeId,
    clearedNodeIds: asStringArray(value.clearedNodeIds, (id) => nodeIds.has(id)),
    squadSize,
    squadMax,
    roster,
    fallen,
    wipeLosses,
    reserves,
    modIds: asStringArray(value.modIds, (id) => fieldModById(id) !== undefined),
    opsEarned: asWholeNumber(value.opsEarned, 0),
    kills: asWholeNumber(value.kills, 0),
    escalation: Math.min(asWholeNumber(value.escalation, 0), 9),
    outcome,
  };
}

function migrateV1(value: Record<string, unknown>): SaveData {
  const base = defaults();
  const spentOnLegacyNodes = asStringArray(value.unlockedNodes, (id) => id in LEGACY_NODE_COSTS)
    .reduce((sum, id) => sum + (LEGACY_NODE_COSTS[id] ?? 0), 0);
  return {
    ...base,
    opsPoints: asWholeNumber(value.opsPoints, 0) + spentOnLegacyNodes,
    career: normalizeCareer(value.career),
  };
}

// v2 predates the campaign layer. Its completed runs were fought before
// theaters escalated — credit them to STONE WALL so specialist taskings the
// player already saw stay unlocked, and start the campaign clock at the
// missions already flown.
function campaignFromV2(career: CareerStats): CampaignState {
  const campaign = defaultCampaign();
  campaign.day = career.missions;
  campaign.theaters["stone-wall"] = { escalation: 0, clears: career.runsCompleted };
  return campaign;
}

export function normalizeSave(value: unknown): SaveData {
  const base = defaults();
  if (!isRecord(value)) return base;
  if (value.version !== 2 && value.version !== 3) return migrateV1(value);

  const career = normalizeCareer(value.career);
  const campaign = value.version === 2 ? campaignFromV2(career) : normalizeCampaign(value.campaign);
  return {
    version: 3,
    opsPoints: asWholeNumber(value.opsPoints, 0),
    armoryUnlocks: asStringArray(value.armoryUnlocks, (id) => armoryUnlockById(id) !== undefined),
    career,
    operators: normalizeOperators(value.operators),
    campaign,
    log: value.version === 2 ? [] : normalizeLog(value.log),
    mail: value.version === 2 ? defaultMail() : normalizeMail(value.mail),
    activeRun: normalizeRun(value.activeRun, unlockedNodeKinds(totalClears(campaign))),
  };
}

// The node graph is derivable from the seed — strip it before persisting so
// saves stay small and generation stays the single source of truth.
function serialize(data: SaveData): string {
  const run = data.activeRun;
  return JSON.stringify({
    ...data,
    activeRun: run ? { ...run, nodes: undefined } : null,
  });
}

export function loadSave(): SaveData {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (raw) return normalizeSave(JSON.parse(raw) as unknown);
  } catch {
    // Corrupt save — start fresh
  }
  return defaults();
}

export function writeSave(data: SaveData): void {
  // Validation happens at the load boundary; writers are typed.
  localStorage.setItem(SAVE_KEY, serialize(data));
}

export function resetSave(): void {
  localStorage.removeItem(SAVE_KEY);
}

// Bank a finished run into the profile. The run's ops (earned node by node)
// land here and nowhere else — meta state never mutates mid-run.
//
// Operator outcomes (docs/design/GDD.md §The Squad Roster): survivors and
// non-wipe casualties gain XP for the missions fought; non-wipe casualties
// come home WOUNDED (sit out the next run); wipe losses are KIA — removed
// and replaced by fresh recruits.
export function bankRun(data: SaveData, run: RunState): SaveData {
  const depth = run.clearedNodeIds.length;
  const xpGain = Math.max(1, depth);
  const kia = new Set(run.wipeLosses);
  const wounded = new Set(run.fallen.filter((n) => !kia.has(n)));
  const fought = new Set([...run.roster, ...wounded]);

  let operators = data.operators
    .filter((op) => !kia.has(op.name))
    .map((op) => {
      const next = { ...op };
      if (fought.has(op.name)) next.xp += xpGain;
      if (wounded.has(op.name)) {
        next.status = "wounded";
        next.woundedRuns = 1;
      }
      return next;
    });
  // Replace the KIA with fresh recruits so the roster stays at strength
  const allNames = () => [...operators.map((o) => o.name), ...kia];
  while (operators.length < data.operators.length) {
    operators = [...operators, { name: freshRecruitName(allNames()), xp: 0, status: "ready", woundedRuns: 0 }];
  }

  // The world moves on: fought theater resolves, ignored theaters escalate,
  // and the Operation gets its bulletin-board card (newest first).
  const outcome = run.outcome === "victory" ? "victory" : "defeat";
  const campaign = advanceCampaign(data.campaign, run.theater, outcome, depth);
  const card: AfterActionRecord = {
    day: campaign.day,
    theater: run.theater,
    outcome,
    depth,
    ops: run.opsEarned,
    kills: run.kills,
    fallen: [...run.fallen],
  };

  return {
    ...data,
    opsPoints: data.opsPoints + run.opsEarned,
    career: {
      ...data.career,
      kills: data.career.kills + run.kills,
      missions: data.career.missions + run.clearedNodeIds.length,
      squadLost: data.career.squadLost + run.fallen.length,
      runsCompleted: data.career.runsCompleted + (run.outcome === "victory" ? 1 : 0),
      runsFailed: data.career.runsFailed + (run.outcome === "defeat" ? 1 : 0),
      bestDepth: Math.max(data.career.bestDepth, depth),
    },
    operators,
    campaign,
    log: [card, ...data.log].slice(0, LOG_LIMIT),
    activeRun: null,
  };
}

// Wounds heal between deployments: tick when a NEW operation launches.
export function healOperatorsForNewRun(data: SaveData): SaveData {
  return {
    ...data,
    operators: data.operators.map((op) => {
      if (op.status !== "wounded") return op;
      const left = op.woundedRuns - 1;
      return left <= 0
        ? { ...op, status: "ready", woundedRuns: 0 }
        : { ...op, woundedRuns: left };
    }),
  };
}
